import { useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
  TextInput,
  useCopyToClipboard,
  useToast,
} from '@api-hub/ui';
import { PageHeader } from '../components/Layout';
import { api, describeError } from '../lib/api';
import { useResource } from '../lib/hooks';
import { formatDateTime, relativeTime } from '../lib/format';

interface HubKey {
  id: string;
  name: string;
  environment: string;
  display: string;
  active: boolean;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedReason: string | null;
  lastUsedAt: string | null;
  createdAt: string;
  ipAllowlist: string[];
}

interface ClientApp {
  id: string;
  key: string;
  environment: string;
  keys: HubKey[];
}

export function HubKeysPage({ role }: { role: string }) {
  const apps = useResource<{ items: ClientApp[] }>('/api/admin/client-apps');
  const toast = useToast();
  const [copied, copy] = useCopyToClipboard();
  const [issueFor, setIssueFor] = useState<ClientApp | null>(null);
  const [form, setForm] = useState({
    name: '',
    environment: 'test',
    expiresInDays: '',
    ipAllowlist: '',
  });
  const [issued, setIssued] = useState<{ key: string; app: string } | null>(null);
  const [revoking, setRevoking] = useState<{ id: string; label: string } | null>(null);
  const canWrite = role === 'OWNER' || role === 'ADMIN';

  const rows = (apps.data?.items ?? []).flatMap((app) =>
    app.keys.map((key) => ({ ...key, appKey: app.key, appId: app.id })),
  );

  return (
    <>
      <PageHeader
        title="Klucze Hub"
        description="Klucz jest pokazywany dokładnie raz. Przechowujemy tylko prefiks wyszukiwania, ostatnie cztery znaki i weryfikator HMAC."
      />

      <Callout>
        Rotacja bez przestoju: wydaj drugi klucz, wdroż go, a dopiero potem unieważnij stary. Oba
        działają równolegle tak długo, jak potrzebujesz.
      </Callout>

      {apps.loading ? (
        <Skeleton rows={5} />
      ) : (
        <Card
          tight
          actions={
            canWrite ? (
              <Select
                aria-label="Wydaj klucz dla aplikacji"
                value=""
                onChange={(event) => {
                  const app = (apps.data?.items ?? []).find(
                    (item) => item.id === event.target.value,
                  );
                  if (app) {
                    setIssueFor(app);
                    setForm({
                      name: '',
                      environment: app.environment,
                      expiresInDays: '',
                      ipAllowlist: '',
                    });
                  }
                }}
              >
                <option value="">Wydaj nowy klucz…</option>
                {(apps.data?.items ?? []).map((app) => (
                  <option key={app.id} value={app.id}>
                    {app.key}
                  </option>
                ))}
              </Select>
            ) : undefined
          }
        >
          <DataTable
            rows={rows}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                title="Brak kluczy"
                description="Wydaj klucz aplikacji, aby mogła wywoływać Hub."
              />
            }
            columns={[
              {
                key: 'app',
                header: 'Aplikacja',
                render: (row) => <span className="ah-mono">{row.appKey}</span>,
              },
              { key: 'name', header: 'Nazwa', render: (row) => row.name },
              {
                key: 'display',
                header: 'Klucz',
                render: (row) => <span className="ah-mono">{row.display}</span>,
              },
              {
                key: 'env',
                header: 'Środowisko',
                render: (row) => (
                  <Badge tone={row.environment === 'production' ? 'danger' : 'neutral'}>
                    {row.environment}
                  </Badge>
                ),
              },
              {
                key: 'status',
                header: 'Status',
                render: (row) => (
                  <>
                    <Badge tone={row.active ? 'ok' : 'danger'}>
                      {row.active ? 'aktywny' : row.revokedAt ? 'unieważniony' : 'wygasł'}
                    </Badge>
                    {row.revokedReason && (
                      <div style={{ fontSize: 11, color: 'var(--ah-text-subtle)' }}>
                        {row.revokedReason}
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: 'lastUsed',
                header: 'Ostatnio użyty',
                render: (row) => (row.lastUsedAt ? relativeTime(row.lastUsedAt) : 'nigdy'),
              },
              {
                key: 'expires',
                header: 'Wygasa',
                render: (row) => (row.expiresAt ? formatDateTime(row.expiresAt) : 'bezterminowo'),
              },
              {
                key: 'actions',
                header: '',
                align: 'right',
                render: (row) =>
                  canWrite && row.active ? (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() =>
                        setRevoking({ id: row.id, label: `${row.appKey}/${row.name}` })
                      }
                    >
                      Unieważnij
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>
      )}

      <Modal
        open={issueFor !== null}
        onOpenChange={(open) => !open && setIssueFor(null)}
        title="Wydaj klucz Hub"
        description={issueFor ? `Aplikacja ${issueFor.key}` : ''}
        footer={
          <>
            <Button onClick={() => setIssueFor(null)}>Anuluj</Button>
            <Button
              variant="primary"
              disabled={!form.name}
              onClick={async () => {
                if (!issueFor) return;
                try {
                  const result = await api.post<{ plaintextKey: string }>(
                    `/api/admin/client-apps/${issueFor.id}/keys`,
                    {
                      name: form.name,
                      environment: form.environment,
                      ...(form.expiresInDays ? { expiresInDays: Number(form.expiresInDays) } : {}),
                      ipAllowlist: form.ipAllowlist
                        .split(',')
                        .map((entry) => entry.trim())
                        .filter(Boolean),
                    },
                  );
                  setIssued({ key: result.plaintextKey, app: issueFor.key });
                  setIssueFor(null);
                  apps.reload();
                } catch (error) {
                  const described = describeError(error);
                  toast.error(described.title, described.body);
                }
              }}
            >
              Wydaj
            </Button>
          </>
        }
      >
        <Field label="Nazwa klucza" hint="Np. „produkcja-2026-08”.">
          {(id) => (
            <TextInput
              id={id}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          )}
        </Field>
        <Field label="Środowisko" hint="Prefiks ah_live_ dla produkcji, ah_test_ dla pozostałych.">
          {(id) => (
            <Select
              id={id}
              value={form.environment}
              onChange={(event) => setForm({ ...form, environment: event.target.value })}
            >
              <option value="test">test</option>
              <option value="staging">staging</option>
              <option value="production">production</option>
            </Select>
          )}
        </Field>
        <Field
          label="Ważność (dni)"
          hint="Puste = bezterminowo. Zalecane ustawienie daty wygaśnięcia."
        >
          {(id) => (
            <TextInput
              id={id}
              type="number"
              value={form.expiresInDays}
              onChange={(event) => setForm({ ...form, expiresInDays: event.target.value })}
            />
          )}
        </Field>
        <Field
          label="Dozwolone adresy IP"
          hint="Lista rozdzielona przecinkami. Puste = bez ograniczeń."
        >
          {(id) => (
            <TextInput
              id={id}
              value={form.ipAllowlist}
              onChange={(event) => setForm({ ...form, ipAllowlist: event.target.value })}
            />
          )}
        </Field>
      </Modal>

      <Modal
        open={issued !== null}
        onOpenChange={(open) => !open && setIssued(null)}
        title="Klucz wydany"
        description={issued ? `Aplikacja ${issued.app}` : ''}
        footer={
          <Button variant="primary" onClick={() => setIssued(null)}>
            Skopiowałem, zamknij
          </Button>
        }
      >
        <Callout tone="danger">
          Ta wartość jest pokazywana tylko raz. Nie da się jej odzyskać — jeśli ją zgubisz, wydaj
          nowy klucz i unieważnij ten.
        </Callout>
        <pre className="ah-code">{issued?.key}</pre>
        <Button onClick={() => issued && copy(issued.key)}>
          {copied ? 'Skopiowano' : 'Kopiuj do schowka'}
        </Button>
      </Modal>

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => !open && setRevoking(null)}
        title="Unieważnić klucz?"
        description={
          revoking
            ? `Klucz ${revoking.label} przestanie działać natychmiast. Wszystkie wywołania nim uwierzytelnione zwrócą INVALID_HUB_KEY.`
            : ''
        }
        confirmLabel="Unieważnij"
        destructive
        confirmPhrase="UNIEWAŻNIJ"
        onConfirm={async () => {
          if (!revoking) return;
          try {
            await api.post(`/api/admin/hub-keys/${revoking.id}/revoke`, {
              reason: 'Unieważniony z panelu',
            });
            toast.success('Klucz unieważniony');
            apps.reload();
          } catch (error) {
            const described = describeError(error);
            toast.error(described.title, described.body);
          }
        }}
      />
    </>
  );
}
